package com.milta.core.network

import com.milta.core.auth.TokenStorage
import io.ktor.client.HttpClient
import io.ktor.client.plugins.websocket.WebSockets
import io.ktor.client.plugins.websocket.webSocket
import io.ktor.websocket.Frame
import io.ktor.websocket.readText
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import kotlinx.coroutines.withTimeoutOrNull
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonPrimitive

/** SignalR JSON-hub-protocol message delimiter (ASCII record separator, `0x1E`). */
private const val RECORD_SEPARATOR = '\u001E'
private val HANDSHAKE_REQUEST = "{\"protocol\":\"json\",\"version\":1}$RECORD_SEPARATOR"
private const val ROUTE_CHANGED_EVENT = "RouteChanged"

/** Reconnect backoff: starts at 2s, doubles up to a 30s cap, reset after every successful connect. */
private const val INITIAL_RECONNECT_DELAY_MS = 2_000L
private const val MAX_RECONNECT_DELAY_MS = 30_000L

/**
 * Liveness watchdog: `AddSignalR()` defaults to a 15s server keep-alive ping and a 30s
 * `ClientTimeoutInterval` (see `Program.cs`), so a healthy connection always yields *some* frame
 * (a real message or the server's keep-alive) at least every ~15s. If no frame arrives within this
 * window, the socket is treated as dead — this is what catches a half-open connection that never
 * delivers a close frame or throws (e.g. Android Doze / carrier NAT silently dropping an idle
 * socket), since `incoming.receive()` alone would otherwise suspend forever in that case.
 */
private const val FRAME_RECEIVE_TIMEOUT_MS = 35_000L

/**
 * Minimal client for the `RouteHub` SignalR hub (`/hubs/routes`, MIL-38 — live refresh of the
 * Manager team dashboard). No SignalR SDK exists for Kotlin Multiplatform, so this
 * hand-rolls only the slice of the protocol the backend actually uses: a JSON-protocol handshake
 * over a raw WebSocket, then record-separator (`0x1E`) -delimited JSON invocation messages. The
 * payload is intentionally not surfaced beyond the changed `routeId` — callers react by re-fetching
 * `GET /api/v1/routes` rather than trusting this channel to carry the full response shape.
 *
 * Scope note (MIL-38): reflects "connected or not", not per-stop delivery guarantees — no
 * client-side keep-alive ping is sent (the server's own periodic pings are enough to prove
 * liveness); instead a receive-side watchdog ([FRAME_RECEIVE_TIMEOUT_MS]) verifies those pings
 * actually keep arriving and treats a stall as a dead connection. [connect] retries with an
 * exponential backoff (capped) for as long as the caller's [CoroutineScope] stays alive, so a
 * dropped connection (network loss, backgrounding, server restart) recovers on its own without the
 * caller having to leave and re-enter the screen.
 */
class RouteRealtimeClient(private val tokenStorage: TokenStorage) {

    private val client: HttpClient = createHttpClient().config { install(WebSockets) }

    private val _isConnected = MutableStateFlow(false)
    /** True while the hub socket is up. No UI consumer since the "En direct" badge was removed
     * (retour 28/10) — kept as the connection state of this client. */
    val isConnected: StateFlow<Boolean> = _isConnected.asStateFlow()

    /** Emits the `routeId` from each `RouteChanged` server event. */
    private val _routeChanged = MutableSharedFlow<String>(extraBufferCapacity = 8)
    val routeChanged: SharedFlow<String> = _routeChanged

    private var job: Job? = null

    /**
     * Connects within [scope] — cancelling [scope] (e.g. `MviViewModel.clear()`) tears the socket down.
     * Reconnects automatically with an exponential backoff (capped, reset on every successful connect)
     * whenever the socket drops for any reason, as long as [scope] is still active.
     */
    fun connect(scope: CoroutineScope) {
        if (job?.isActive == true) return
        job = scope.launch(Dispatchers.Default) {
            var backoffMs = INITIAL_RECONNECT_DELAY_MS
            while (isActive) {
                val token = tokenStorage.getAccessToken()
                if (token == null) {
                    _isConnected.value = false
                    return@launch
                }
                runCatching {
                    client.webSocket(wsUrl(token)) {
                        send(Frame.Text(HANDSHAKE_REQUEST))
                        val ack = (incoming.receive() as? Frame.Text)?.readText().orEmpty()
                        if (ack.contains("\"error\"")) return@webSocket
                        _isConnected.value = true
                        backoffMs = INITIAL_RECONNECT_DELAY_MS
                        try {
                            while (true) {
                                val frame = withTimeoutOrNull(FRAME_RECEIVE_TIMEOUT_MS) { incoming.receive() }
                                    ?: error(
                                        "No frame (not even a server keep-alive) within " +
                                            "${FRAME_RECEIVE_TIMEOUT_MS}ms — assuming a half-open socket"
                                    )
                                if (frame is Frame.Text) handleFrame(frame.readText())
                            }
                        } finally {
                            _isConnected.value = false
                        }
                    }
                }
                _isConnected.value = false
                if (!isActive) break
                delay(backoffMs)
                backoffMs = (backoffMs * 2).coerceAtMost(MAX_RECONNECT_DELAY_MS)
            }
        }
    }

    /** Explicit disconnect — usually unnecessary since [connect] is tied to the caller's [scope]. */
    fun disconnect() {
        job?.cancel()
        job = null
        _isConnected.value = false
    }

    // internal (not private) so RouteRealtimeClientTest can drive the parser directly with
    // synthetic frames instead of needing a real/fake WebSocket transport.
    internal fun handleFrame(raw: String) {
        raw.split(RECORD_SEPARATOR)
            .filter { it.isNotBlank() }
            .forEach { message ->
                val json = runCatching { Json.parseToJsonElement(message) }.getOrNull() as? JsonObject
                    ?: return@forEach
                if (json["target"]?.jsonPrimitive?.contentOrNull != ROUTE_CHANGED_EVENT) return@forEach
                val routeId = (json["arguments"] as? JsonArray)
                    ?.firstOrNull()
                    ?.let { it as? JsonObject }
                    ?.get("routeId")?.jsonPrimitive?.contentOrNull
                    ?: ""
                _routeChanged.tryEmit(routeId)
            }
    }

    private fun wsUrl(token: String): String {
        val httpBase = baseUrl.trimEnd('/')
        val wsBase = when {
            httpBase.startsWith("https://") -> "wss://" + httpBase.removePrefix("https://")
            httpBase.startsWith("http://") -> "ws://" + httpBase.removePrefix("http://")
            else -> httpBase
        }
        return "$wsBase/hubs/routes?access_token=$token"
    }
}
